/**
 * Parses one line of a pasted card list into { quantity, name }.
 * Handles two formats:
 * - MTGO-style: "1 Sol Ring" -> { quantity: 1, name: "Sol Ring" }
 * - Moxfield-style export: "1 Chishiro, the Shattered Blade (NEC) 1" -> { quantity: 1, name: "Chishiro, the Shattered Blade" }
 *   (found by cutting at the LAST " (" in the line, after the quantity prefix is stripped)
 */
function parseCardListLine(line) {
  let name = line.trim();
  let quantity = 1;

  const quantityMatch = name.match(/^([0-9]+)\s(.*)/);
  if (quantityMatch) {
    quantity = parseInt(quantityMatch[1], 10);
    name = quantityMatch[2].trim();
  }

  const lastParenIndex = name.lastIndexOf(' (');
  if (lastParenIndex !== -1) {
    name = name.slice(0, lastParenIndex).trim();
  }

  return { quantity, name };
}

function parseCardListText(text) {
  return (text || '')
    .split('\n')
    .map(parseCardListLine)
    .filter(entry => entry.name.length > 0);
}

function buildDeckQuantities(text) {
  const quantities = {};
  parseCardListText(text).forEach(({ quantity, name }) => {
    const key = name.toLowerCase();
    quantities[key] = (quantities[key] || 0) + quantity;
  });
  return quantities;
}

/**
 * Sets how many copies of a card the list holds, editing the text in place.
 *
 * Deliberately not a rebuild via buildCopyListText: that only knows about cards
 * the server resolved, so it would silently drop the lines surfaced in
 * "Cartas no encontradas" and reorder everything else. Only the leading quantity
 * of the matching line is rewritten, so a Moxfield-style
 * "1 Chishiro, the Shattered Blade (NEC) 1" keeps its set and collector number.
 *
 * quantity <= 0 removes the card. A card that isn't in the list yet is appended.
 */
function setCardQuantity(text, name, quantity) {
  const target = (name || '').trim().toLowerCase();
  if (!target) return text || '';

  const lines = (text || '').split('\n');
  const nextLines = [];
  let applied = false;

  lines.forEach(line => {
    if (parseCardListLine(line).name.toLowerCase() !== target) {
      nextLines.push(line);
      return;
    }
    // Only the first matching line is kept: buildDeckQuantities sums duplicates,
    // so leaving the rest would make the displayed count disagree with the edit.
    if (applied) return;
    applied = true;
    if (quantity <= 0) return;
    const withoutQuantity = line.trim().replace(/^\d+\s+/, '');
    nextLines.push(`${quantity} ${withoutQuantity}`);
  });

  const nextText = nextLines.join('\n');
  if (applied || quantity <= 0) return nextText;

  // Appending: an all-whitespace list must not leave a blank first line, but any
  // blank lines the user put between sections are left alone.
  const newLine = `${quantity} ${name.trim()}`;
  return nextText.trim() ? `${nextText}\n${newLine}` : newLine;
}

function buildCopyListText(cards, quantities) {
  return cards
    .map(card => {
      const quantity = quantities[card.name.toLowerCase()] || 1;
      return `${quantity} ${card.name}`;
    })
    .join('\n');
}

const cardListParser = {
  parseCardListLine,
  parseCardListText,
  buildDeckQuantities,
  setCardQuantity,
  buildCopyListText,
};

export default cardListParser;
